/**
 * FL Product Packs Modal
 * Multi-step modal for pack selection
 */

(function() {
    'use strict';

    class PacksModal {
        constructor(container) {
            this.container = container;
            this.modal = container.querySelector('[data-pm-modal]');
            this.trigger = container.querySelector('[data-pm-trigger]');
            this.closeButtons = container.querySelectorAll('[data-pm-close]');
            this.prevBtn = container.querySelector('[data-pm-prev]');
            this.nextHeaderBtn = container.querySelector('[data-pm-next-header]');
            this.footer = container.querySelector('[data-pm-footer]');
            this.footerBtn = this.footer?.querySelector('.button');
            this.progressFill = container.querySelector('[data-pm-progress]');
            this.currentStepEl = container.querySelector('[data-pm-current-step]');
            this.titleEl = container.querySelector('[data-pm-title]');
            this.steps = container.querySelectorAll('[data-pm-step]');
            this.packs = container.querySelectorAll('[data-pm-pack]');
            // Step 2: variant composer elements (uses existing fl-variant-composer snippet)
            this.variants = container.querySelectorAll('[data-variant-item]');
            this.selectedEl = container.querySelector('[data-added-savors]');
            this.maxEl = container.querySelector('[data-max-savors]');
            
            // Step 3 elements
            this.offers = container.querySelectorAll('[data-pm-offer]');
            this.singlePriceEl = container.querySelector('[data-pm-single-price]');
            this.subOriginalEl = container.querySelector('[data-pm-sub-original]');
            this.subPriceEl = container.querySelector('[data-pm-sub-price]');
            this.freeDeliveryBanner = container.querySelector('[data-pm-free-delivery]');
            
            // Step 4 elements
            this.recapEl = container.querySelector('[data-pm-recap]');
            this.crossSellSection = container.querySelector('[data-pm-cross-sell-section]');
            this.crossSellItems = container.querySelectorAll('[data-pm-cross-sell]');
            this.crossSellNotice = container.querySelector('[data-pm-cross-sell-notice]');

            // Get config from data attributes
            this.totalSteps = parseInt(container.dataset.totalSteps) || 4;
            this.variantCount = parseInt(container.dataset.variantCount) || 1;
            this.hasSubscriptions = container.dataset.hasSubscriptions === 'true';
            this.subscriptionDiscount = parseFloat(container.dataset.subscriptionDiscount) || 0;
            this.firstSellingPlanId = container.dataset.firstSellingPlan || '';
            this.titles = JSON.parse(container.dataset.titles || '{}');
            this.translations = JSON.parse(container.dataset.translations || '{}');
            this.productTitle = container.dataset.productTitle || '';
            this.productImage = container.dataset.productImage || '';
            this.variantsData = JSON.parse(container.dataset.variants || '[]');
            this.packTiers = JSON.parse(container.dataset.packTiers || '[]');

            // State
            this.currentStep = 1;
            this.selectedPackQty = 1;
            this.selectedVariants = {};
            this.selectedOfferType = 'single'; // 'single' or 'subscription'
            this.hasFreeDelivery = false;
            this.selectedCrossSell = [];

            this.init();
        }

        init() {
            this.bindEvents();
            this.initSelectedPack();
            this.filterCrossSellByCart(); // Initial filter based on cart state
        }

        bindEvents() {
            // Open modal
            this.trigger?.addEventListener('click', () => this.open());

            // Close modal (overlay + close button)
            this.closeButtons.forEach(btn => {
                btn.addEventListener('click', () => this.close());
            });

            // Navigation
            this.prevBtn?.addEventListener('click', () => this.prevStep());
            this.nextHeaderBtn?.addEventListener('click', () => this.handleNext());
            this.footerBtn?.addEventListener('click', () => this.handleNext());

            // Pack selection
            this.packs.forEach(pack => {
                pack.addEventListener('click', () => this.selectPack(pack));
            });

            // Variant selection
            this.variants.forEach(variant => {
                this.bindVariantEvents(variant);
            });
            
            // Offer type selection (step 3)
            this.offers.forEach(offer => {
                offer.addEventListener('click', () => this.selectOffer(offer));
            });
            
            // Cross-sell selection (step 4)
            // The <label> element already toggles the checkbox natively
            // We just need to listen for changes and update the selection
            this.crossSellItems.forEach(item => {
                const checkbox = item.querySelector('input[type="checkbox"]');
                if (checkbox) {
                    checkbox.addEventListener('change', () => this.updateCrossSellSelection());
                }
            });
            
            // Listen for cart changes to update cross-sell visibility
            document.addEventListener('cart:refresh', () => this.filterCrossSellByCart());
        }
        
        updateCrossSellSelection() {
            this.selectedCrossSell = [];
            this.crossSellItems.forEach(item => {
                const checkbox = item.querySelector('input[type="checkbox"]');
                if (checkbox?.checked && !item.classList.contains('hidden')) {
                    this.selectedCrossSell.push({
                        variantId: item.dataset.variantId,
                        price: parseFloat(item.dataset.price) || 0
                    });
                }
            });
            this.updateRecapTotal();
        }
        
        async filterCrossSellByCart() {
            if (!this.crossSellSection || this.crossSellItems.length === 0) return;
            
            try {
                const response = await fetch('/cart.js');
                const cart = await response.json();
                
                // Get all product handles in cart
                const cartProductHandles = new Set(
                    cart.items.map(item => item.handle)
                );
                
                let visibleCount = 0;
                
                this.crossSellItems.forEach(item => {
                    const productHandle = item.dataset.productHandle;
                    const isInCart = cartProductHandles.has(productHandle);
                    
                    if (isInCart) {
                        item.classList.add('hidden');
                        // Uncheck if was checked
                        const checkbox = item.querySelector('input[type="checkbox"]');
                        if (checkbox) checkbox.checked = false;
                    } else {
                        item.classList.remove('hidden');
                        visibleCount++;
                    }
                });
                
                // Hide entire section if no products to show
                this.crossSellSection.style.display = visibleCount > 0 ? '' : 'none';
                
                // Update selection in case a checked item was hidden
                this.updateCrossSellSelection();
                
            } catch (error) {
                console.error('Error fetching cart for cross-sell filter:', error);
            }
        }
        
        getDiscountForQuantity(totalQty) {
            // Find the best matching pack tier for the given quantity
            // Sort tiers by quantity descending to find the highest applicable tier
            const sortedTiers = [...this.packTiers].sort((a, b) => b.qty - a.qty);
            
            for (const tier of sortedTiers) {
                if (totalQty >= tier.qty) {
                    return {
                        discount: tier.discount,
                        freeDelivery: tier.freeDelivery
                    };
                }
            }
            
            // No tier found, return 0 discount
            return { discount: 0, freeDelivery: false };
        }
        
        renderRecap() {
            if (!this.recapEl) return;
            
            // Get discount based on current total quantity
            const totalQty = this.getTotalSelected();
            const tierInfo = this.getDiscountForQuantity(totalQty);
            const packDiscount = tierInfo.discount;
            const discountPercent = packDiscount > 0 ? Math.round(packDiscount * 100) : 0;
            
            // Update free delivery status based on current tier
            this.hasFreeDelivery = tierInfo.freeDelivery;
            this.updateFreeDeliveryBanner();
            
            // Hide all recap items first
            const recapItems = this.recapEl.querySelectorAll('[data-pm-recap-item]');
            recapItems.forEach(item => item.style.display = 'none');
            
            // Show and update selected variants
            for (const [variantId, qty] of Object.entries(this.selectedVariants)) {
                if (qty > 0) {
                    const recapItem = this.recapEl.querySelector(`[data-pm-recap-item="${variantId}"]`);
                    if (recapItem) {
                        recapItem.style.display = 'block';
                        
                        // Get variant unit price from data attribute
                        const lineItemEl = recapItem.querySelector('[data-variant-price]');
                        const variantUnitPrice = parseFloat(lineItemEl?.dataset.variantPrice) || 0;
                        
                        // Calculate line price: unit price × qty × pack discount × subscription discount
                        let originalLinePrice = variantUnitPrice * qty;
                        let finalLinePrice = originalLinePrice * (1 - packDiscount);
                        
                        if (this.selectedOfferType === 'subscription' && this.subscriptionDiscount > 0) {
                            finalLinePrice = finalLinePrice * (1 - this.subscriptionDiscount / 100);
                        }
                        
                        const lineSavings = originalLinePrice - finalLinePrice;
                        
                        // Update quantity
                        const qtyInput = recapItem.querySelector('[data-recap-qty-input]');
                        if (qtyInput) qtyInput.value = qty;
                        
                        // Update discount badge
                        const badge = recapItem.querySelector('[data-recap-badge]');
                        const badgeText = recapItem.querySelector('[data-recap-badge-text]');
                        if (badge && badgeText && discountPercent > 0) {
                            badge.style.display = 'flex';
                            badgeText.textContent = `${discountPercent}% collagène`;
                        } else if (badge) {
                            badge.style.display = 'none';
                        }
                        
                        // Update prices
                        const priceEl = recapItem.querySelector('[data-recap-price]');
                        const originalPriceEl = recapItem.querySelector('[data-recap-original-price]');
                        const savingsEl = recapItem.querySelector('[data-recap-savings]');
                        
                        if (priceEl) priceEl.textContent = this.formatMoney(finalLinePrice);
                        
                        if (originalPriceEl && lineSavings > 0) {
                            originalPriceEl.style.display = 'inline';
                            originalPriceEl.textContent = this.formatMoney(originalLinePrice);
                        } else if (originalPriceEl) {
                            originalPriceEl.style.display = 'none';
                        }
                        
                        if (savingsEl && lineSavings > 0) {
                            savingsEl.style.display = 'flex';
                            savingsEl.querySelector('span').textContent = `${this.translations.saveAmount || 'Économisez'} ${this.formatMoney(lineSavings)}`;
                        } else if (savingsEl) {
                            savingsEl.style.display = 'none';
                        }
                        
                        // Bind events if not already bound
                        if (!recapItem.dataset.eventsBound) {
                            this.bindRecapItemEvents(recapItem, variantId);
                            recapItem.dataset.eventsBound = 'true';
                        }
                    }
                }
            }
        }
        
        bindRecapItemEvents(recapItem, variantId) {
            const decreaseBtn = recapItem.querySelector('[data-recap-qty-decrease]');
            const increaseBtn = recapItem.querySelector('[data-recap-qty-increase]');
            const removeBtn = recapItem.querySelector('[data-recap-remove]');
            
            decreaseBtn?.addEventListener('click', (e) => {
                e.stopPropagation();
                if (this.selectedVariants[variantId] > 1) {
                    this.selectedVariants[variantId]--;
                    this.selectedPackQty--;
                    this.renderRecap();
                    this.updateRecapTotal();
                }
            });
            
            increaseBtn?.addEventListener('click', (e) => {
                e.stopPropagation();
                this.selectedVariants[variantId]++;
                this.selectedPackQty++;
                this.renderRecap();
                this.updateRecapTotal();
            });
            
            removeBtn?.addEventListener('click', (e) => {
                e.stopPropagation();
                const qty = this.selectedVariants[variantId] || 0;
                this.selectedPackQty -= qty;
                delete this.selectedVariants[variantId];
                this.renderRecap();
                this.updateRecapTotal();
            });
        }
        
        updateRecapTotal() {
            // Get discount based on current total quantity
            const totalQty = this.getTotalSelected();
            const tierInfo = this.getDiscountForQuantity(totalQty);
            const packDiscount = tierInfo.discount;
            
            // Calculate total based on actual selected variants and quantities
            let originalTotal = 0;
            let discountedTotal = 0;
            
            for (const [variantId, qty] of Object.entries(this.selectedVariants)) {
                if (qty > 0) {
                    const recapItem = this.recapEl?.querySelector(`[data-pm-recap-item="${variantId}"]`);
                    const lineItemEl = recapItem?.querySelector('[data-variant-price]');
                    const variantUnitPrice = parseFloat(lineItemEl?.dataset.variantPrice) || 0;
                    
                    const lineOriginal = variantUnitPrice * qty;
                    const lineDiscounted = lineOriginal * (1 - packDiscount);
                    
                    originalTotal += lineOriginal;
                    discountedTotal += lineDiscounted;
                }
            }
            
            // Apply subscription discount if selected
            let finalTotal = discountedTotal;
            if (this.selectedOfferType === 'subscription' && this.subscriptionDiscount > 0) {
                finalTotal = discountedTotal * (1 - this.subscriptionDiscount / 100);
            }
            
            // Add cross-sell prices
            let crossSellTotal = 0;
            this.selectedCrossSell.forEach(item => {
                crossSellTotal += item.price;
            });
            
            const total = finalTotal + crossSellTotal;
            const originalWithCrossSell = originalTotal + crossSellTotal;
            
            // Update button text with total (only on recap step)
            if (this.footerBtn && this.currentStep === this.totalSteps) {
                const hasDiscount = packDiscount > 0 || crossSellTotal > 0 || (this.selectedOfferType === 'subscription' && this.subscriptionDiscount > 0);
                const originalPriceHtml = hasDiscount && originalWithCrossSell > total
                    ? `<s>${this.formatMoney(originalWithCrossSell)}</s> `
                    : '';
                this.footerBtn.innerHTML = `${this.translations.addToCart} - ${originalPriceHtml}${this.formatMoney(total)}`;
            }
        }

        bindVariantEvents(variant) {
            // Uses existing fl-variant-composer snippet structure
            const addWrap = variant.querySelector('.add-button-wrapper');
            const addBtn = variant.querySelector('.fl-variant-item__add-btn');
            const qtyPicker = variant.querySelector('.quantity-picker');
            const qtyInput = variant.querySelector('.quantity-input');
            const minusBtn = variant.querySelector('[data-decrease]');
            const plusBtn = variant.querySelector('[data-increase]');
            const variantId = variant.dataset.variantId;

            addBtn?.addEventListener('click', (e) => {
                e.stopPropagation();
                if (this.getTotalSelected() >= this.selectedPackQty) return;

                this.selectedVariants[variantId] = 1;
                qtyInput.value = 1;
                addWrap.classList.add('hidden');
                qtyPicker.classList.remove('hidden');
                this.updateVariantState();
            });

            minusBtn?.addEventListener('click', (e) => {
                e.stopPropagation();
                let val = parseInt(qtyInput.value) || 0;
                if (val > 0) {
                    val--;
                    qtyInput.value = val;
                    if (val === 0) {
                        delete this.selectedVariants[variantId];
                        addWrap.classList.remove('hidden');
                        qtyPicker.classList.add('hidden');
                    } else {
                        this.selectedVariants[variantId] = val;
                    }
                    this.updateVariantState();
                }
            });

            plusBtn?.addEventListener('click', (e) => {
                e.stopPropagation();
                if (this.getTotalSelected() >= this.selectedPackQty) return;

                let val = parseInt(qtyInput.value) || 0;
                val++;
                qtyInput.value = val;
                this.selectedVariants[variantId] = val;
                this.updateVariantState();
            });
        }

        initSelectedPack() {
            // Find initial pack via wrapper with active child
            const initialPack = this.container.querySelector('[data-pm-pack]:has(.fl-product-pack.active)') 
                || this.container.querySelector('[data-pm-pack]');
            if (initialPack) {
                this.selectedPackQty = parseInt(initialPack.dataset.packQuantity) || 1;
                this.hasFreeDelivery = initialPack.dataset.freeDelivery === 'true';
                this.selectedPackPrice = parseFloat(initialPack.dataset.packPrice) || 0;
                if (this.maxEl) this.maxEl.textContent = this.selectedPackQty;
                
                // Update free delivery banner visibility
                this.updateFreeDeliveryBanner();
            }
        }
        
        updateFreeDeliveryBanner() {
            if (this.freeDeliveryBanner) {
                this.freeDeliveryBanner.style.display = this.hasFreeDelivery ? 'block' : 'none';
            }
        }

        open() {
            // Move modal to body to escape any stacking context
            if (this.modal.parentNode !== document.body) {
                document.body.appendChild(this.modal);
            }
            this.modal.classList.add('active');
            document.body.style.overflow = 'hidden';
            this.filterCrossSellByCart(); // Filter cross-sell based on current cart
            this.updateFreeDeliveryBanner(); // Ensure banner reflects current pack selection
            this.goToStep(1);
        }

        close() {
            this.modal.classList.remove('active');
            document.body.style.overflow = '';
        }

        selectPack(packWrapper) {
            // Remove active from all pack cards
            this.packs.forEach(p => {
                const packCard = p.querySelector('.fl-product-pack');
                if (packCard) packCard.classList.remove('active');
            });
            // Add active to selected pack card
            const selectedCard = packWrapper.querySelector('.fl-product-pack');
            if (selectedCard) selectedCard.classList.add('active');
            
            this.selectedPackQty = parseInt(packWrapper.dataset.packQuantity) || 1;
            this.hasFreeDelivery = packWrapper.dataset.freeDelivery === 'true';
            this.selectedPackPrice = parseFloat(packWrapper.dataset.packPrice) || 0;
            
            if (this.maxEl) this.maxEl.textContent = this.selectedPackQty;
            this.updateVariantState();
            this.updateOfferPrices();
            this.updateFreeDeliveryBanner();

            window.igEvents.push({ event: `offer-change-${this.selectedPackQty}` });
        }
        
        selectOffer(offerEl) {
            // Update UI
            this.offers.forEach(o => o.classList.remove('selected'));
            offerEl.classList.add('selected');
            
            // Update radio
            const radio = offerEl.querySelector('input[type="radio"]');
            if (radio) radio.checked = true;
            
            // Update state
            this.selectedOfferType = offerEl.dataset.pmOffer;
            
            // Show/hide cross-sell subscription notice
            this.updateCrossSellNotice();
        }
        
        updateCrossSellNotice() {
            if (this.crossSellNotice) {
                const isSubscription = this.selectedOfferType === 'subscription';
                this.crossSellNotice.style.display = isSubscription ? 'block' : 'none';
            }
        }
        
        updateOfferPrices() {
            // Use modal instead of container since modal is moved to body on open
            const selectedPack = this.modal.querySelector('[data-pm-pack]:has(.fl-product-pack.active)');
            if (!selectedPack) return;
            
            const packPrice = parseFloat(selectedPack.dataset.packPrice) || 0;
            
            // Update single price
            if (this.singlePriceEl) {
                this.singlePriceEl.textContent = this.formatMoney(packPrice);
            }
            
            // Update subscription prices
            if (this.subOriginalEl) {
                this.subOriginalEl.textContent = this.formatMoney(packPrice);
            }
            if (this.subPriceEl) {
                const subPrice = packPrice * (1 - this.subscriptionDiscount / 100);
                this.subPriceEl.textContent = this.formatMoney(subPrice);
            }
            
            // Update free delivery banner
            this.updateFreeDeliveryBanner();
        }
        
        formatMoney(amountInCents) {
            if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
                return Shopify.formatMoney(amountInCents, window.theme?.moneyFormat || '{{amount_with_comma_separator}}€');
            }
            // Fallback: convert cents to euros
            return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amountInCents / 100);
        }

        getTotalSelected() {
            return Object.values(this.selectedVariants).reduce((a, b) => a + b, 0);
        }

        updateVariantState() {
            const total = this.getTotalSelected();
            if (this.selectedEl) this.selectedEl.textContent = total;

            if (this.currentStep === 2) {
                const isComplete = total === this.selectedPackQty;
                if (this.footerBtn) {
                    this.footerBtn.disabled = !isComplete;
                    // If subscriptions exist, show "Next step", otherwise "Add to cart"
                    if (isComplete) {
                        this.footerBtn.textContent = this.hasSubscriptions 
                            ? this.translations.nextStep 
                            : this.translations.addToCart;
                    } else {
                        this.footerBtn.textContent = this.translations.nextStep;
                    }
                }
                if (this.nextHeaderBtn) {
                    this.nextHeaderBtn.disabled = !isComplete;
                }
            }
        }

        goToStep(step) {
            if (step < 1 || step > this.totalSteps) return;

            // If only 1 variant, skip step 2
            if (this.variantCount <= 1 && step === 2) {
                const onlyVariant = this.variants[0];
                if (onlyVariant) {
                    this.selectedVariants[onlyVariant.dataset.variantId] = this.selectedPackQty;
                }
                // Go to step 3 if subscriptions exist, otherwise add to cart
                if (this.hasSubscriptions) {
                    step = 3;
                } else {
                    this.addToCart();
                    return;
                }
            }

            this.currentStep = step;

            // Update steps visibility
            this.steps.forEach(s => {
                s.classList.toggle('active', parseInt(s.dataset.pmStep) === step);
            });

            // Update progress
            const progress = (step / this.totalSteps) * 100;
            this.progressFill.style.width = progress + '%';

            // Update step indicator
            this.currentStepEl.textContent = step;

            // Update title
            if (this.titles[step]) {
                this.titleEl.textContent = this.titles[step];
            }

            // Update nav buttons
            this.prevBtn.disabled = step === 1;

            if (step === 2) {
                this.updateVariantState();
            } else if (step === 3 && this.hasSubscriptions) {
                // Update prices and free delivery banner for step 3 (offer selection)
                this.updateOfferPrices();
                if (this.footerBtn) {
                    this.footerBtn.disabled = false;
                    this.footerBtn.textContent = this.translations.nextStep;
                }
                if (this.nextHeaderBtn) {
                    this.nextHeaderBtn.disabled = false;
                }
            } else if (step === this.totalSteps) {
                // Recap step (final step)
                this.renderRecap();
                this.updateRecapTotal();
                this.updateFreeDeliveryBanner();
                this.updateCrossSellNotice();
                if (this.nextHeaderBtn) {
                    this.nextHeaderBtn.disabled = true; // No next on last step
                }
            } else {
                if (this.footerBtn) {
                    this.footerBtn.disabled = false;
                    this.footerBtn.textContent = this.translations.nextStep;
                }
                if (this.nextHeaderBtn) {
                    this.nextHeaderBtn.disabled = false;
                }
            }
        }

        prevStep() {
            if (this.currentStep > 1) {
                this.goToStep(this.currentStep - 1);
            }
        }

        handleNext() {
            if (this.currentStep === 1) {
                this.goToStep(2);
            } else if (this.currentStep === 2) {
                // After flavor selection, go to offer selection if subscriptions exist
                if (this.hasSubscriptions) {
                    this.goToStep(3);
                } else {
                    // Skip to recap (which is step 3 when no subscriptions)
                    this.goToStep(this.totalSteps);
                }
            } else if (this.currentStep === 3 && this.hasSubscriptions) {
                // After offer selection, go to recap
                this.goToStep(4);
            } else if (this.currentStep === this.totalSteps) {
                // Final step - add to cart
                this.addToCart();
            }
        }

        addToCart() {
            const items = [];
            // Use modal instead of container since modal is moved to body on open
            const selectedPack = this.modal.querySelector('[data-pm-pack]:has(.fl-product-pack.active)');
            const discount = selectedPack?.dataset.packDiscount || 0;
            
            // Selling plan: must match the pack duration (metafield id_abonnement on each pack).
            // data-first-selling-plan is only the first product plan — wrong when another pack is selected.
            const packPlanRaw = selectedPack?.dataset?.subscriptionId;
            const sellingPlanId =
                packPlanRaw != null && String(packPlanRaw).trim() !== ''
                    ? packPlanRaw
                    : this.firstSellingPlanId;
            const isSubscription = this.selectedOfferType === 'subscription' && sellingPlanId;

            for (const [variantId, qty] of Object.entries(this.selectedVariants)) {
                if (qty > 0) {
                    const item = {
                        id: parseInt(variantId),
                        quantity: qty,
                        properties: {
                            '_pack_discount': discount
                        }
                    };
                    
                    // Add selling_plan if subscription selected
                    if (isSubscription) {
                        item.selling_plan = parseInt(sellingPlanId, 10);
                    }
                    
                    items.push(item);
                }
            }
            
            // Add cross-sell products
            this.selectedCrossSell.forEach(cs => {
                items.push({
                    id: parseInt(cs.variantId),
                    quantity: 1
                });
            });

            if (items.length === 0) return;

            if (this.footerBtn) {
                this.footerBtn.disabled = true;
                this.footerBtn.textContent = this.translations.adding;
            }

            fetch('/cart/add.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ items })
            })
            .then(res => res.json())
            .then(() => {
                this.close();
                this.resetSelection();
                document.dispatchEvent(new CustomEvent('cart:refresh'));

                const cartDrawer = document.querySelector('cart-drawer');
                if (cartDrawer?.show) cartDrawer.show();
            })
            .catch(err => {
                console.error('Cart error:', err);
                if (this.footerBtn) {
                    this.footerBtn.disabled = false;
                    this.footerBtn.textContent = this.translations.addToCart;
                }
            });
        }
        
        resetSelection() {
            // Reset state
            this.selectedVariants = {};
            this.selectedCrossSell = [];
            this.selectedOfferType = 'single';
            
            // Reset variant composer UI
            this.variants.forEach(variant => {
                const addWrap = variant.querySelector('.add-button-wrapper');
                const qtyPicker = variant.querySelector('.quantity-picker');
                const qtyInput = variant.querySelector('.quantity-input');
                
                if (addWrap) addWrap.classList.remove('hidden');
                if (qtyPicker) qtyPicker.classList.add('hidden');
                if (qtyInput) qtyInput.value = 0;
            });
            
            // Reset offer selection UI
            this.offers.forEach(offer => {
                offer.classList.remove('selected');
                const radio = offer.querySelector('input[type="radio"]');
                if (radio) radio.checked = false;
            });
            // Use modal instead of container since modal is moved to body on open
            const singleOffer = this.modal.querySelector('[data-pm-offer="single"]');
            if (singleOffer) {
                singleOffer.classList.add('selected');
                const radio = singleOffer.querySelector('input[type="radio"]');
                if (radio) radio.checked = true;
            }
            
            // Reset cross-sell checkboxes
            this.crossSellItems.forEach(item => {
                const checkbox = item.querySelector('input[type="checkbox"]');
                if (checkbox) checkbox.checked = false;
            });
            
            // Update counter
            if (this.selectedEl) this.selectedEl.textContent = '0';
        }
    }

    // Initialize
    function init() {
        const containers = document.querySelectorAll('.fl-pm-container');
        containers.forEach(container => {
            if (!container.dataset.pmInitialized) {
                new PacksModal(container);
                container.dataset.pmInitialized = 'true';
            }
        });
    }

    // Run on DOM ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    // Re-init on Shopify section reload
    document.addEventListener('shopify:section:load', init);
})();
